import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Video, VideoOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// One sortable consultation-mode row: label, the Requires-link/No-link-needed toggle, and delete.
// Mirrors EntryTypeRow's layout/drag/delete conventions; the requiresLink toggle copies the
// icon-toggle-button convention from SubCategoryRow's showToPatient (Eye/EyeOff) control.
export function ConsultationModeRow({ mode, disabled = false, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: mode.id,
  });
  const requiresLink = Boolean(mode.requiresLink);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 shadow-sm',
        isDragging ? 'opacity-60 shadow-md ring-2 ring-ring/50' : '',
      )}
    >
      <button
        type="button"
        aria-label="Drag to reorder consultation mode"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab rounded text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <Input
        value={mode.label}
        onChange={(e) => onChange({ label: e.target.value })}
        maxLength={60}
        disabled={disabled}
        className="h-8 w-64 text-sm font-medium"
        placeholder="Consultation mode"
      />
      <div className="flex-1" />
      <button
        type="button"
        onClick={() => onChange({ requiresLink: !requiresLink })}
        disabled={disabled}
        title="Whether joining this consultation mode requires a video/call link"
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          requiresLink
            ? 'bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/40 dark:text-emerald-400'
            : 'text-muted-foreground hover:bg-muted',
        )}
      >
        {requiresLink ? <Video className="h-3 w-3" aria-hidden /> : <VideoOff className="h-3 w-3" aria-hidden />}
        {requiresLink ? 'Requires link' : 'No link needed'}
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        aria-label={`Delete consultation mode ${mode.label}`}
        className={cn(
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
        )}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
